// Unpublished Work © 2023-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {getInventories} from 'Services/membership-service';
import {deletePartTypes, getPartTypes} from 'Services/inventory-service';
import {fetchEffectData, useDeepEffect} from 'Utils/react-utils';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import EditLink from 'Ui/components/common/data-table/edit-link';
import AddButton from 'Ui/components/settings/common/add-button';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {MANAGE_EQUIPMENT_SETTINGS} from 'Common/constants/business-activities';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';

function getDataGridColumns(translations, openPartTypeDialog, partTypes, fetchPartTypes, inventoryId) {
    return [
        {
            headerName: translations.ONLINK_PART_TYPE,
            field: 'title',
            flex: 1,
            renderCell: (params) => {
                const {
                    row,
                    value
                } = params;

                return (
                    <EditLink
                        onClick={() => openPartTypeDialog({
                            partType: row,
                            inventoryPartTypes: partTypes,
                            inventoryId,
                            translations,
                            fetchPartTypes
                        })}
                    >
                        {value}
                    </EditLink>
                );
            }

        }
    ];
}

function getFilterComponent(translations, inventoryId, openPartTypeDialog, fetchPartTypes, partTypes, loading) {
    return (
        <div className='extra-table-options'>
            <AddButton
                disabled={loading}
                label={replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
                    '0': translations.ONLINK_PART_TYPE
                })}
                onClick={() => openPartTypeDialog({
                    inventoryId,
                    inventoryPartTypes: partTypes,
                    translations,
                    fetchPartTypes
                })}
            />
        </div>
    );
}

function PartTypesSettings(props) {
    const {
        closeConfirmation,
        openConfirmation,
        openPartTypeDialog,
        translations
    } = props;

    const {addToast} = useToast();
    const [loading, setLoading] = React.useState(true);
    const [partTypes, setPartTypes] = React.useState([]);
    const [inventoryId, setInventoryId] = React.useState(null);

    async function fetchPartTypes(isMounted = () => true) {
        setLoading(true);

        const {inventories} = await getInventories();
        const inventoryId = inventories?.length ? inventories[0].inventoryId : null;
        const {partTypes} = inventoryId ? await getPartTypes(inventoryId) : [];

        if (isMounted()) {
            setPartTypes(partTypes);
            setInventoryId(inventoryId);
            setLoading(false);
        }
    }

    function deletePartTypeHandler(partType) {
        openConfirmation({
            message: replaceTranslationNames(translations.ONLINK_ITEM_THIS_CANNOT_BE_UNDONE, {
                '0': translations.ONLINK_PART_TYPE.toLowerCase()
            }),
            onCancel: closeConfirmation,
            showLoadingIcon: true,
            title: replaceTranslationNames(translations.ONLINK_DELETE_ITEM, {
                '0': translations.ONLINK_PART_TYPE
            }),
            async onContinue() {
                try {
                    await deletePartTypes(partType.inventoryId, partType.partTypeId);
                } catch {
                    addToast({
                        message: replaceTranslationNames(translations.ONLINK_CONFIRM_ITEM_DELETION_FAILED, {
                            '0': translations.ONLINK_PART_TYPE.toLowerCase()
                        }),
                        type: TOAST_TYPE.ERROR
                    });
                }

                closeConfirmation();
                await fetchPartTypes();
            }
        });
    }

    useDeepEffect(() => fetchEffectData(fetchPartTypes), []);

    return (
        <div
            className='settings-container data-grid'
            data-testid='settings-container'
        >
            <DataGridWrapper
                columns={getDataGridColumns(translations, openPartTypeDialog, partTypes, fetchPartTypes, inventoryId)}
                deleteHandler={deletePartTypeHandler}
                filterComponent={getFilterComponent(translations, inventoryId, openPartTypeDialog, fetchPartTypes, partTypes, loading)}
                getRowId={(row) => row.partTypeId}
                initialState={{
                    sorting: {
                        sortModel: [
                            {
                                field: 'title',
                                sort: 'asc'
                            }
                        ]
                    }
                }}
                loading={loading}
                noDataAvailableMessage={translations.ONLINK_NO_PART_TYPES_FOUND}
                rows={partTypes}
                searchFields={['title']}
                searchable={true}
                showDeleteColumn={isAuthorized({
                    myJdPermissions: MANAGE_EQUIPMENT_SETTINGS
                }, props)}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
        </div>
    );
}

PartTypesSettings.propTypes = {
    closeConfirmation: PropTypes.func,
    isMigrated: PropTypes.bool,
    myJdPermissions: PropTypes.myJdPermissions,
    openConfirmation: PropTypes.func,
    openPartTypeDialog: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        isMigrated: state.membership.isMigrated,
        myJdPermissions: state.account.myJdPermissions,
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openPartTypeDialog(props) {
            dispatch(openDialog(dialogTypes.PART_TYPE_DIALOG, props));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(PartTypesSettings);
